import React, { useEffect, useState } from "react";
import { FaUpload, FaTrash } from "react-icons/fa6";
import { PuffLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { add_template, remove_template, messageClear } from "../store/reducers/templateReducer";
import { initialTags, adminIds } from "../utils/helper";
import useTemplates from "../hooks/useTemplates";
import useUser from "../hooks/useUser";
import { useNavigate } from "react-router-dom";
import { serverTimestamp } from "firebase/firestore";

function CreateTemplate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: user, isLoading } = useUser();
  const {
    data: templates,
    isLoading: templatesIsLoading,
    refetch: templatesRefetch,
  } = useTemplates();

  const [formData, setFormData] = useState({
    title: "",
    imageURL: null,
  });

  const [imageAsset, setImageAsset] = useState({
    isImageLoading: false,
    uri: null,
    image: null,
  });

  const [selectedTags, setSelectedTags] = useState([]);

  const { successMessage, errorMessage } = useSelector((state) => state.template);

  useEffect(() => {
    if (!isLoading && !adminIds.includes(user?.uid)) {
      navigate("/", { replace: true });
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
      setImageAsset({ ...imageAsset, uri: null, image: null });
      setFormData({ title: "", imageURL: null });
      setSelectedTags([]);
      templatesRefetch();
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      title: e.target.value,
    }));
  };

  const isAllowed = (file) => {
    return ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAllowed(file)) {
      toast.error("Only JPG/PNG allowed");
      return;
    }
    setImageAsset({
      image: file,
      uri: URL.createObjectURL(file),
      isImageLoading: false,
    });
  };

  const deleteImage = () => {
    setImageAsset({ isImageLoading: false, uri: null, image: null });
  };

  const handleSelectTags = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const pushToCloud = async () => {
    if (!formData.title || !imageAsset.image) {
      return toast.error("Title and Image Required");
    }

    const fm = new FormData();
    fm.append("title", formData.title);
    fm.append("image", imageAsset.image);
    fm.append("tags", selectedTags);
    fm.append(
      "name",
      templates && templates.length > 0
        ? `Template${templates.length + 1}`
        : "Template1"
    );
    fm.append("timestamp", serverTimestamp());

    dispatch(add_template(fm));
  };

  const removeTemplate = (template) => {
    dispatch(remove_template(template));
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-10 px-6 flex gap-10 justify-center">

      {/* LEFT CARD */}
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md p-6 flex flex-col gap-6 h-fit">

        <h2 className="text-xl font-bold text-gray-800">Create Template</h2>

        <div className="flex justify-between text-sm text-gray-500">
          <span>Template ID:</span>
          <span className="font-semibold text-gray-800">
            {templates?.length > 0
              ? `Template${templates.length + 1}`
              : "Template1"}
          </span>
        </div>

        {/* Title */}
        <input
          type="text"
          placeholder="Template Title"
          value={formData.title}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:ring-2 ring-blue-500"
        />

        {/* Upload */}
        <label
          className="w-full h-72 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition"
        >
          {imageAsset.uri ? (
            <div className="relative w-full h-full">
              <img
                src={imageAsset.uri}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage();
                }}
              >
                <FaTrash size={12} />
              </button>
            </div>
          ) : (
            <div className="text-center flex flex-col items-center gap-3">
              <FaUpload className="text-3xl text-gray-400" />
              <p className="text-gray-500 text-sm">Click to upload</p>
            </div>
          )}

          <input type="file" className="hidden" accept=".jpg,.jpeg,.png" onChange={handleFileSelect} />
        </label>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {initialTags.map((tag) => (
            <span
              key={tag}
              onClick={() => handleSelectTags(tag)}
              className={`px-3 py-1 border rounded-full text-xs cursor-pointer transition ${
                selectedTags.includes(tag)
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Save */}
        <button
          onClick={pushToCloud}
          className="w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700 transition"
        >
          Save Template
        </button>
      </div>

      {/* RIGHT GRID */}
      <div className="flex-1 max-w-4xl">

        {templatesIsLoading ? (
          <div className="flex justify-center items-center h-full">
            <PuffLoader color="#3b82f6" />
          </div>
        ) : templates?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div key={template._id} className="group relative rounded-xl overflow-hidden shadow-sm bg-white">
                <img
                  src={template.imageUrl}
                  className="w-full h-72 object-cover transition group-hover:scale-105 duration-300"
                />

                <button
                  onClick={() => removeTemplate(template)}
                  className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full opacity-90 hover:opacity-100 transition"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center flex-col items-center gap-4 py-16">
            <PuffLoader color="#3b82f6" />
            <p className="text-gray-500">No Templates Found</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateTemplate;
